import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    ToggleButtonGroup,
    ToggleButton,
    Paper,
} from "@mui/material";

// Components
import Icon from "@/theme/vectors/bundle";
import SlidePanel from "@/theme/partials/slidepanel";
import MediaGrid from "./MediaGrid";
import MediaList from "./MediaList";
import FileUpload from "./FileUpload";

// Utils
import { VALID_MIME_TYPES } from "@/utils/products/constants";

const MediaPanel = ({ 
    isOpen, 
    onClose, 
    medias, 
    activeVariantId, 
    variants = [],
    onSelectMediaForVariant,
    selectedFiles,
    uploading,
    onFileSelect,
    onFileChange,
    onFileUpload,
    onCancelUpload,
    onClearSelectedFiles,
    onOpenDeleteModal,
    fileInputRef,
    t
}) => {
    // Media view state (list or grid)
    const [mediaViewMode, setMediaViewMode] = useState("list");
    
    // Handle media view mode change
    const handleMediaViewModeChange = (event, newMode) => {
        if (newMode !== null) {
            setMediaViewMode(newMode);
        }
    };
    
    return (
        <SlidePanel
            title={activeVariantId ? t('product.detail.selectMedia', 'Select Media') : t('product.detail.media', 'Media')}
            isOpen={isOpen}
            onClose={onClose}
        >
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {!activeVariantId ? (
                    <Button
                        variant="contained"
                        startIcon={<Icon name="upload" />}
                        onClick={onFileSelect}
                        disabled={uploading}
                        sx={{ 
                            bgcolor: '#8D6E63',
                            '&:hover': {
                                bgcolor: '#6D4C41'
                            },
                            textTransform: 'uppercase'
                        }}
                    >
                        {uploading ? t('product.detail.uploading', 'UPLOADING...') : t('product.detail.uploadMedia', 'TẢI LÊN MEDIA')}
                    </Button>
                ) : (
                    <Typography variant="subtitle1">
                        {t('product.detail.selectMedia', 'Select Media')}
                    </Typography>
                )}
                
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onFileChange}
                    multiple
                    accept={VALID_MIME_TYPES.join(',')}
                />
                
                {!activeVariantId && (
                    <ToggleButtonGroup
                        value={mediaViewMode}
                        exclusive
                        onChange={handleMediaViewModeChange}
                        aria-label="media view mode"
                        size="small"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <Icon name="listView" />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <Icon name="gridView" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>
            
            {/* Filename format rules note */}
            {!activeVariantId && (
                <Paper sx={{ p: 1, mb: 2, bgcolor: '#f5f7ff' }}>
                    <Typography variant="caption" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name="info" size={16} sx={{ mr: 0.5, color: 'info.main' }} />
                        {t('product.detail.filenameRules', 'Filenames must only contain English letters, numbers, hyphens (-) and underscores (_). No spaces or special characters allowed.')}
                    </Typography>
                </Paper>
            )}
            
            {/* File upload component */}
            {selectedFiles.length > 0 && (
                <FileUpload 
                    selectedFiles={selectedFiles}
                    uploading={uploading}
                    onFileUpload={onFileUpload}
                    onCancelUpload={onCancelUpload}
                    onClearSelectedFiles={onClearSelectedFiles}
                    t={t}
                />
            )}
            
            {/* Existing media display */}
            {medias && medias.length > 0 ? (
                activeVariantId ? (
                    <MediaGrid 
                        medias={medias}
                        activeVariantId={activeVariantId}
                        variants={variants}
                        onSelectMediaForVariant={onSelectMediaForVariant}
                        isSelectionMode={true}
                        t={t}
                    />
                ) : (
                    mediaViewMode === 'grid' ? (
                        <MediaGrid 
                            medias={medias}
                            onOpenDeleteModal={onOpenDeleteModal}
                            t={t}
                        />
                    ) : (
                        <MediaList 
                            medias={medias}
                            onOpenDeleteModal={onOpenDeleteModal}
                            t={t}
                        />
                    )
                )
            ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>{t('product.detail.noMedia')}</Typography>
                </Paper>
            )}
        </SlidePanel>
    );
};

export default MediaPanel;